import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { tendersService } from '../services/tendersService'
import { formatDate, formatDateTime } from '../utils/helpers'

/**
 * Tenders Page
 * High density, fluid table layout with server-side pagination.
 */
export default function Tenders() {
    const navigate = useNavigate()
    const [tenders, setTenders] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [sourceFilter, setSourceFilter] = useState('')
    const [sources, setSources] = useState([])
    const [page, setPage] = useState(1)
    const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0 })

    const [debouncedSearch, setDebouncedSearch] = useState('')

    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(search.trim())
            setPage(1)
        }, 400)
        return () => clearTimeout(t)
    }, [search])

    const fetchPage = useCallback(async (p, source = sourceFilter, status = statusFilter) => {
        setLoading(true)
        setError(null)
        try {
            const result = await tendersService.getPage(p, 50, source, status === 'all' ? '' : status, debouncedSearch)
            setTenders(result.data)
            setPagination(result.pagination)
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }, [sourceFilter, statusFilter, debouncedSearch])

    useEffect(() => {
        fetchPage(page)
    }, [page, fetchPage])

    useEffect(() => {
        tendersService.getSources().then(setSources).catch(() => setSources([]))
    }, [])

    const filtered = tenders || []


    const totalPages = Math.ceil((pagination.total || 0) / (pagination.pageSize || 50))

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header with Search & Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-admin-heading tracking-tight">Tenders Market</h1>
                    <p className="text-admin-muted text-sm font-medium mt-0.5">{pagination.total?.toLocaleString()} total opportunities</p>
                </div>

                <div className="flex items-center gap-3 flex-1 sm:flex-none">
                    <div className="relative flex-1 sm:w-64">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-4 w-4 text-admin-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                        </div>
                        <input
                            type="text"
                            placeholder="Search tenders..."
                            className="w-full pl-9 pr-4 py-2 text-sm input-admin bg-white shadow-sm"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>

                    <div className="relative">
                        <select
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
                            className="select-admin font-bold pl-3 pr-8 py-2"
                        >
                            <option value="all">All Status</option>
                            <option value="open">Open</option>
                            <option value="closed">Closed</option>
                            <option value="expired">Expired</option>
                        </select>
                    </div>

                    <div className="relative">
                        <select
                            value={sourceFilter}
                            onChange={(e) => { setSourceFilter(e.target.value); setPage(1) }}
                            className="select-admin font-bold pl-3 pr-8 py-2"
                        >
                            <option value="">All Sources</option>
                            {sources.map((src) => (
                                <option key={src} value={src}>{src}</option>
                            ))}
                        </select>
                    </div>

                    <button onClick={() => fetchPage(page)} className="btn-secondary shadow-sm whitespace-nowrap">
                        Refresh
                    </button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="p-3 bg-status-error-bg border border-status-error/20 text-status-error text-sm rounded font-bold">
                    {error}
                </div>
            )}

            {/* Table View */}
            <div className="card-admin">
                <div className="overflow-x-auto">
                    <table className="table-admin w-full text-left text-sm">
                        <thead>
                            <tr>
                                <th className="px-4 py-3 w-28 whitespace-nowrap">ID</th>
                                <th className="px-4 py-3">Title</th>
                                <th className="px-4 py-3">Publisher</th>
                                <th className="px-4 py-3 w-24 whitespace-nowrap">Status</th>
                                <th className="px-4 py-3 w-48 whitespace-nowrap">Source</th>
                                <th className="px-4 py-3 text-right w-28 whitespace-nowrap">Published</th>
                                <th className="px-4 py-3 text-right w-28 whitespace-nowrap">Deadline</th>
                                <th className="px-4 py-3 text-right w-40 whitespace-nowrap">Created At</th>
                                <th className="px-4 py-3 text-right w-40 whitespace-nowrap">Updated At</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                            {loading ? (
                                <tr><td colSpan={9} className="p-12 text-center text-admin-muted font-bold">Loading tenders...</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={9} className="p-12 text-center text-admin-muted font-bold">No tenders found.</td></tr>
                            ) : (
                                filtered.map((tender) => (
                                    <tr
                                        key={tender.fullId || tender.id}
                                        className="hover:bg-blue-50/50 transition-colors group align-top cursor-pointer"
                                        onClick={() => navigate(`/tenders/${tender.fullId}`)}
                                    >
                                        <td className="px-4 py-3 font-mono text-xs font-bold text-admin-muted border-l-4 border-transparent group-hover:border-brand-navy whitespace-nowrap">
                                            {tender.id}
                                        </td>
                                        <td className="px-4 py-3 font-semibold text-brand-navy group-hover:underline group-hover:text-blue-600">
                                            <span className="leading-snug">{tender.title}</span>
                                        </td>
                                        <td className="px-4 py-3 text-admin-text font-medium text-xs">
                                            {tender.publisher}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <TenderStatusBadge status={tender.status} />
                                        </td>
                                        <td className="px-4 py-3 text-admin-text font-medium whitespace-nowrap text-xs">{tender.source}</td>
                                        <td className="px-4 py-3 text-right text-admin-muted tabular-nums whitespace-nowrap text-xs">
                                            {formatDate(tender.published || tender.published_date || tender.publishedDate)}
                                        </td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap text-xs">
                                            <span className={`font-bold tabular-nums ${tender.status === 'expired' || tender.status === 'closed' ? 'text-admin-muted' : 'text-status-error'}`}>
                                                {formatDate(tender.deadline)}
                                                {console.log("Deadline raw:", tender.deadline),
console.log("Formatted:", formatDate(tender.deadline))}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right text-admin-muted tabular-nums whitespace-nowrap text-xs">
                                            {formatDateTime(tender.createdAt)}
                                        </td>
                                        <td className="px-4 py-3 text-right text-admin-muted tabular-nums whitespace-nowrap text-xs">
                                            {formatDateTime(tender.updatedAt)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {/* Pagination Footer */}
                <div className="px-4 py-3 border-t border-admin-border bg-gray-50 flex items-center justify-between">
                    <span className="text-xs font-bold text-admin-muted">
                        Page {pagination.page} of {totalPages || 1} ({pagination.total?.toLocaleString()} results)
                    </span>
                    <div className="flex gap-2">
                        <button
                            disabled={page <= 1}
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            className={`btn-secondary px-3 py-1 text-xs ${page <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            Previous
                        </button>
                        <button
                            disabled={page >= totalPages}
                            onClick={() => setPage(p => p + 1)}
                            className={`btn-secondary px-3 py-1 text-xs ${page >= totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

function TenderStatusBadge({ status }) {
    const styles = {
        open: 'badge-success',
        closed: 'badge-neutral',
        expired: 'badge-error',
    }
    const style = styles[status] || styles.closed

    return (
        <span className={`badge ${style}`}>
            {status}
        </span>
    )
}
